import type {
  Application,
  AuditEvent,
  BankFormRequest,
  BankFormTemplate,
  BankProduct,
  ClientTask,
  DemoUser,
  RuleDefinition
} from "../types";

export const demoUsers: DemoUser[] = [
  {
    id: "user-client-1",
    role: "client",
    name: "Мария Георгиева",
    email: "client@mg-credit.demo",
    title: "Клиент",
    description: "Попълва заявление, приема документи и вижда предварителна оценка.",
    linkedApplicationId: "app-1001"
  },
  {
    id: "user-consultant-1",
    role: "consultant",
    name: "Елена Стоянова",
    email: "consultant@mg-credit.demo",
    title: "Кредитен консултант",
    description: "Преглежда входящите заявки, липсващи документи и банковите съвпадения."
  },
  {
    id: "user-admin-1",
    role: "admin",
    name: "Администратор",
    email: "admin@mg-credit.demo",
    title: "Администратор правила",
    description: "Управлява продуктови правила, версии на документи и настройки за продукционна среда."
  }
];

export const bankProducts: BankProduct[] = [
  {
    id: "ubb-home-purchase",
    bankName: "ОББ",
    productName: "Ипотечен кредит за покупка",
    productType: "mortgage",
    headline: "Индикативна променлива лихва от 2.28% за суми над 100 000 EUR при превод на доход.",
    minRate: 2.28,
    maxRate: 2.8,
    aprFrom: 3.03,
    maxAmount: 500000,
    maxTermYears: 30,
    maxLtv: 0.85,
    maxDti: 0.55,
    minIncome: 1200,
    minEmploymentMonths: 6,
    acceptedStages: ["act16", "existing"],
    requiresSalaryTransfer: true,
    supportsOnlineFlow: true,
    strengths: ["Много силно съвпадение при висок доход", "Подходяща за покупка над 100 000 EUR"],
    cautions: ["По-рискова при ранно строителство", "Нужен е превод на доход за най-добрия сценарий"],
    sourceUrl: "https://www.ubb.bg/individual-clients/credits/za-nedvijim-imot",
    sourceLabel: "Официална страница ОББ",
    lastChecked: "2026-07-08"
  },
  {
    id: "postbank-home",
    bankName: "Пощенска банка",
    productName: "Жилищен ипотечен кредит",
    productType: "mortgage",
    headline: "До 500 000 EUR, срок до 30 години, финансиране до 85%, лихва от 2.70% до 3.95%.",
    minRate: 2.7,
    maxRate: 3.95,
    maxAmount: 500000,
    maxTermYears: 30,
    maxLtv: 0.85,
    maxDti: 0.52,
    minIncome: 1100,
    minEmploymentMonths: 6,
    acceptedStages: ["act15", "act16", "existing"],
    requiresSalaryTransfer: false,
    supportsOnlineFlow: true,
    strengths: ["Гъвкав продукт за покупка, строеж или ремонт", "Добър резервен вариант при стандартен профил"],
    cautions: ["По-висок горен край на лихвения диапазон", "Изисква внимателна проверка на такси"],
    sourceUrl: "https://www.postbank.bg/Mortgage-Loans/Mortgage-loan",
    sourceLabel: "Официална страница Пощенска банка",
    lastChecked: "2026-07-08"
  },
  {
    id: "unicredit-fixed-5",
    bankName: "УниКредит Булбанк",
    productName: "Ипотечен кредит с фиксирана лихва",
    productType: "mortgage",
    headline: "Фиксирана лихва 2.89% за първите 5 години, финансиране до 85%, срок до 30 години.",
    minRate: 2.89,
    maxRate: 3.35,
    maxAmount: 600000,
    maxTermYears: 30,
    maxLtv: 0.85,
    maxDti: 0.5,
    minIncome: 1400,
    minEmploymentMonths: 12,
    acceptedStages: ["act16", "existing"],
    requiresSalaryTransfer: true,
    supportsOnlineFlow: true,
    strengths: ["Силен аргумент при клиенти, които искат предвидима вноска", "Висок лимит и дълъг срок"],
    cautions: ["По-строг филтър за заетост", "След фиксирания период условията трябва да се преизчислят"],
    sourceUrl:
      "https://www.unicreditbulbank.bg/bg/individualni-klienti/krediti/ipotechni-krediti/ipotechen-kredit-s-fiksiran-lihven-procent-promo/",
    sourceLabel: "Официална страница УниКредит Булбанк",
    lastChecked: "2026-07-08"
  },
  {
    id: "dsk-home-purchase",
    bankName: "Банка ДСК",
    productName: "Ипотечен кредит за покупка на имот",
    productType: "mortgage",
    headline: "Представителен пример с 112 000 EUR, 30 години и ГПР 3.89% при пакетни условия.",
    minRate: 2.95,
    maxRate: 3.89,
    aprFrom: 3.89,
    maxAmount: 450000,
    maxTermYears: 30,
    maxLtv: 0.8,
    maxDti: 0.5,
    minIncome: 1200,
    minEmploymentMonths: 6,
    acceptedStages: ["act15", "act16", "existing"],
    requiresSalaryTransfer: true,
    supportsOnlineFlow: true,
    strengths: ["Добър стандартен продукт за домакинства с превод на доход", "Има онлайн ипотечен портал"],
    cautions: ["LTV прагът е по-консервативен в демо правилата", "Пакетните условия трябва да се валидират"],
    sourceUrl:
      "https://dskbank.bg/%D0%B8%D0%BD%D0%B4%D0%B8%D0%B2%D0%B8%D0%B4%D1%83%D0%B0%D0%BB%D0%BD%D0%B8-%D0%BA%D0%BB%D0%B8%D0%B5%D0%BD%D1%82%D0%B8/%D0%BA%D1%80%D0%B5%D0%B4%D0%B8%D1%82%D0%B8%D1%80%D0%B0%D0%BD%D0%B5/%D0%B6%D0%B8%D0%BB%D0%B8%D1%89%D0%BD%D0%B8-%D0%B8-%D0%B8%D0%BF%D0%BE%D1%82%D0%B5%D1%87%D0%BD%D0%B8-%D0%BA%D1%80%D0%B5%D0%B4%D0%B8%D1%82%D0%B8",
    sourceLabel: "Официална страница Банка ДСК",
    lastChecked: "2026-07-08"
  },
  {
    id: "allianz-home",
    bankName: "Алианц Банк",
    productName: "Жилищен кредит",
    productType: "mortgage",
    headline: "Представителен пример: 2.60% годишен лихвен процент и ГПР 2.86% при превод на заплата.",
    minRate: 2.6,
    maxRate: 3.1,
    aprFrom: 2.86,
    maxAmount: 350000,
    maxTermYears: 30,
    maxLtv: 0.8,
    maxDti: 0.48,
    minIncome: 1500,
    minEmploymentMonths: 12,
    acceptedStages: ["act16", "existing"],
    requiresSalaryTransfer: true,
    supportsOnlineFlow: false,
    strengths: ["Добър продукт за нисък риск", "Подходящ за клиенти с чист доход и ниска задлъжнялост"],
    cautions: ["По-строги демо прагове за DTI и доход", "Процесът е по-малко дигитален в демо модела"],
    sourceUrl: "https://www.allianz.bg/bg_BG/individuals/banking/mortgage-loans/home-credit.html",
    sourceLabel: "Официална страница Алианц Банк",
    lastChecked: "2026-07-08"
  },
  {
    id: "procredit-home",
    bankName: "ПроКредит Банк",
    productName: "Ипотечен кредит онлайн",
    productType: "mortgage",
    headline: "Лихва: 6-месечен EURIBOR + 1.25% надбавка, минимум 3.50%, срок до 30 години.",
    minRate: 3.5,
    maxRate: 4.4,
    maxAmount: 400000,
    maxTermYears: 30,
    maxLtv: 0.8,
    maxDti: 0.5,
    minIncome: 1300,
    minEmploymentMonths: 6,
    acceptedStages: ["act16", "existing"],
    requiresSalaryTransfer: true,
    supportsOnlineFlow: true,
    strengths: ["Добър онлайн процес", "Ясна формула EURIBOR + надбавка"],
    cautions: ["Минимална лихва 3.50% в демо продукта", "Не е първи избор при висок LTV"],
    sourceUrl: "https://www.procreditbank.bg/bg/individualni-klienti/krediti/jilishtni-krediti/ipotechen-kredit",
    sourceLabel: "Официална страница ПроКредит Банк",
    lastChecked: "2026-07-08"
  },
  {
    id: "ubb-consumer-mobile",
    bankName: "ОББ",
    productName: "Потребителски кредит дигитално",
    productType: "consumer",
    headline: "Индикативна променлива лихва 5.4% - 6.5% при кандидатстване през мобилно банкиране.",
    minRate: 5.4,
    maxRate: 6.5,
    maxAmount: 50000,
    maxTermYears: 10,
    maxLtv: 1,
    maxDti: 0.45,
    minIncome: 900,
    minEmploymentMonths: 6,
    acceptedStages: ["none"],
    requiresSalaryTransfer: true,
    supportsOnlineFlow: true,
    strengths: ["Бърз дигитален канал", "Подходящ за стабилен доход и ниски задължения"],
    cautions: ["Само EUR нови искания според публичното описание", "По-чувствителен към DTI"],
    sourceUrl: "https://www.ubb.bg/individual-clients/credits/consumer-loan",
    sourceLabel: "Официална страница ОББ",
    lastChecked: "2026-07-08"
  },
  {
    id: "postbank-consumer-online",
    bankName: "Пощенска банка",
    productName: "Потребителски кредит онлайн",
    productType: "consumer",
    headline: "Сума до 50 000 EUR, срок до 120 месеца, лихвен процент 5.99%.",
    minRate: 5.99,
    maxRate: 7.2,
    maxAmount: 50000,
    maxTermYears: 10,
    maxLtv: 1,
    maxDti: 0.48,
    minIncome: 850,
    minEmploymentMonths: 6,
    acceptedStages: ["none"],
    requiresSalaryTransfer: false,
    supportsOnlineFlow: true,
    strengths: ["Ясен онлайн сценарий", "Добър продукт за бърза заявка"],
    cautions: ["Трябва потвърждение на крайни такси", "Демо правилото ограничава висок DTI"],
    sourceUrl: "https://www.postbank.bg/Consumer-Loans",
    sourceLabel: "Официална страница Пощенска банка",
    lastChecked: "2026-07-08"
  },
  {
    id: "allianz-special-consumer",
    bankName: "Алианц Банк",
    productName: "Специален потребителски кредит",
    productType: "consumer",
    headline: "Лихвени условия от 4.45% при нетен месечен доход над 1250 EUR и срок до 84 месеца.",
    minRate: 4.45,
    maxRate: 6.8,
    maxAmount: 50000,
    maxTermYears: 10,
    maxLtv: 1,
    maxDti: 0.42,
    minIncome: 1250,
    minEmploymentMonths: 12,
    acceptedStages: ["none"],
    requiresSalaryTransfer: true,
    supportsOnlineFlow: false,
    strengths: ["Много добро съвпадение за нискорискови потребителски кредити", "Конкурентна демонстрационна лихва"],
    cautions: ["По-високо изискване за доход", "По-строга ръчна проверка"],
    sourceUrl: "https://www.allianz.bg/bg_BG/individuals/banking/retail-credits/special-consumer-credit.html",
    sourceLabel: "Официална страница Алианц Банк",
    lastChecked: "2026-07-08"
  },
  {
    id: "fibank-home-flex",
    bankName: "Първа инвестиционна банка",
    productName: "Гъвкав жилищен кредит",
    productType: "mortgage",
    headline: "Демо продукт за покупка или рефинансиране с по-консервативен LTV и ръчна оценка при променлив доход.",
    minRate: 3.15,
    maxRate: 4.1,
    aprFrom: 3.78,
    maxAmount: 420000,
    maxTermYears: 30,
    maxLtv: 0.8,
    maxDti: 0.5,
    minIncome: 1150,
    minEmploymentMonths: 6,
    acceptedStages: ["act15", "act16", "existing"],
    requiresSalaryTransfer: false,
    supportsOnlineFlow: true,
    strengths: ["Добър резервен маршрут при стандартни ипотечни профили", "Приема акт 15 в демо правилата"],
    cautions: ["По-нисък LTV праг", "Изисква детайлен преглед при собствен бизнес"],
    sourceUrl: "https://www.fibank.bg/bg/chastni-lica/krediti",
    sourceLabel: "Публична продуктова страница Fibank",
    lastChecked: "2026-07-08"
  },
  {
    id: "ccbank-home-standard",
    bankName: "Централна кооперативна банка",
    productName: "Стандартен ипотечен кредит",
    productType: "mortgage",
    headline: "Демо продукт за клиенти със стабилен трудов договор, собствено участие и стандартен имот.",
    minRate: 3.25,
    maxRate: 4.25,
    maxAmount: 300000,
    maxTermYears: 25,
    maxLtv: 0.75,
    maxDti: 0.46,
    minIncome: 1000,
    minEmploymentMonths: 12,
    acceptedStages: ["act16", "existing"],
    requiresSalaryTransfer: true,
    supportsOnlineFlow: false,
    strengths: ["Подходящ за по-ниски искани суми", "Ясен филтър за нискорискови профили"],
    cautions: ["По-кратък срок и по-нисък LTV", "Не е първи избор при ранно строителство"],
    sourceUrl: "https://www.ccbank.bg/bg/individual-clients/credits",
    sourceLabel: "Публична продуктова страница ЦКБ",
    lastChecked: "2026-07-08"
  },
  {
    id: "bacb-green-home",
    bankName: "БАКБ",
    productName: "Зелен жилищен кредит",
    productType: "mortgage",
    headline: "Демо маршрут за енергийно ефективен имот с добър доход и пълен документален комплект.",
    minRate: 2.95,
    maxRate: 3.85,
    maxAmount: 380000,
    maxTermYears: 30,
    maxLtv: 0.82,
    maxDti: 0.49,
    minIncome: 1300,
    minEmploymentMonths: 9,
    acceptedStages: ["act16", "existing"],
    requiresSalaryTransfer: false,
    supportsOnlineFlow: true,
    strengths: ["Добър fit за имоти с готови документи", "Полезен нишов маршрут за зелени критерии"],
    cautions: ["Изисква доказване на характеристиките на имота", "Не приема акт 14 в демо правилата"],
    sourceUrl: "https://www.bacb.bg/bg/individuals/credits",
    sourceLabel: "Публична продуктова страница БАКБ",
    lastChecked: "2026-07-08"
  },
  {
    id: "tbi-consumer-fast",
    bankName: "TBI Bank",
    productName: "Бърз потребителски кредит",
    productType: "consumer",
    headline: "Демо продукт за малки потребителски нужди с бърза дистанционна проверка и по-висок ценови диапазон.",
    minRate: 8.4,
    maxRate: 14.9,
    maxAmount: 25000,
    maxTermYears: 7,
    maxLtv: 1,
    maxDti: 0.4,
    minIncome: 700,
    minEmploymentMonths: 3,
    acceptedStages: ["none"],
    requiresSalaryTransfer: false,
    supportsOnlineFlow: true,
    strengths: ["Бърза предварителна проверка", "Подходящ за малки суми"],
    cautions: ["Не е подходящ за големи суми", "По-чувствителен към текущи задължения"],
    sourceUrl: "https://tbibank.bg/lichni-finansi/krediti/",
    sourceLabel: "Публична продуктова страница TBI Bank",
    lastChecked: "2026-07-08"
  },
  {
    id: "dsk-refinance",
    bankName: "Банка ДСК",
    productName: "Рефинансиране на ипотечен кредит",
    productType: "refinance",
    headline: "Демо продукт за клиенти с история на редовно обслужване и достатъчна остатъчна стойност на имота.",
    minRate: 3.05,
    maxRate: 3.95,
    aprFrom: 4.05,
    maxAmount: 420000,
    maxTermYears: 30,
    maxLtv: 0.78,
    maxDti: 0.48,
    minIncome: 1300,
    minEmploymentMonths: 12,
    acceptedStages: ["act16", "existing"],
    requiresSalaryTransfer: true,
    supportsOnlineFlow: true,
    strengths: ["Добър маршрут при редовна кредитна история", "Позволява консолидация на ипотечни разходи"],
    cautions: ["Изисква проверка на остатък, такси и обезпечение", "Не е подходящо при висок LTV"],
    sourceUrl: "https://dskbank.bg/индивидуални-клиенти/кредитиране",
    sourceLabel: "Публична продуктова страница Банка ДСК",
    lastChecked: "2026-07-08"
  },
  {
    id: "ubb-small-business",
    bankName: "ОББ",
    productName: "Бизнес кредит за малка фирма",
    productType: "business",
    headline: "Демо маршрут за самоосигуряващи се и малки дружества с доказуем оборот и счетоводни отчети.",
    minRate: 5.1,
    maxRate: 8.8,
    maxAmount: 150000,
    maxTermYears: 10,
    maxLtv: 0.7,
    maxDti: 0.45,
    minIncome: 1800,
    minEmploymentMonths: 24,
    acceptedStages: ["none", "existing"],
    requiresSalaryTransfer: false,
    supportsOnlineFlow: false,
    strengths: ["Подходящ за фирмени клиенти с история", "Ясно отделя бизнес риска от личния профил"],
    cautions: ["Изисква отчети, обороти и ръчна оценка", "Не се изпраща автоматично без консултант"],
    sourceUrl: "https://www.ubb.bg/business-clients/credits",
    sourceLabel: "Публична продуктова страница ОББ бизнес",
    lastChecked: "2026-07-08"
  }
];

export const demoApplications: Application[] = [
  {
    id: "app-1001",
    createdAt: "2026-07-08T08:45:00.000Z",
    updatedAt: "2026-07-08T13:20:00.000Z",
    status: "bank_form_requested",
    clientUserId: "user-client-1",
    consultantId: "user-consultant-1",
    applicant: {
      fullName: "Мария Георгиева",
      age: 34,
      city: "София",
      phone: "+359 888 123 456",
      email: "client@mg-credit.demo",
      householdMembers: 3,
      childrenUnder18: 1,
      employmentType: "permanent_contract",
      employmentMonths: 72,
      permanentContract: true,
      salaryTransferPossible: true
    },
    financial: {
      netMonthlyIncome: 4300,
      coApplicantIncome: 1800,
      otherMonthlyIncome: 250,
      existingMonthlyDebt: 220,
      declaredSavings: 72000,
      requestedAmount: 145000,
      propertyValue: 215000,
      ownContribution: 70000,
      termYears: 30,
      purpose: "home_purchase",
      constructionStage: "act16",
      currency: "EUR"
    },
    consents: {
      precontractInfo: true,
      personalDataNotice: true,
      mediationAgreement: true,
      bankSharingConsent: true,
      marketingConsent: false,
      acceptedAt: "2026-07-08T09:04:00.000Z",
      ipAddress: "demo-hidden",
      documentVersion: "2026.07-demo"
    },
    documents: [
      { id: "id-card", label: "Лична карта", status: "received", owner: "client" },
      { id: "income-proof", label: "Служебна бележка / доход", status: "received", owner: "client" },
      { id: "property-offer", label: "Оферта / предварителен договор за имот", status: "received", owner: "client" },
      { id: "bank-statements", label: "Извлечения по сметка 6 месеца", status: "missing", owner: "client" }
    ],
    notes: ["Клиентът иска предвидима вноска за първите години.", "Имотът е с акт 16."],
    preferredBanks: ["ОББ", "УниКредит Булбанк", "Пощенска банка"]
  },
  {
    id: "app-1002",
    createdAt: "2026-07-07T11:25:00.000Z",
    updatedAt: "2026-07-08T10:12:00.000Z",
    status: "needs_review",
    consultantId: "user-consultant-1",
    applicant: {
      fullName: "Иван Петров",
      age: 41,
      city: "Пловдив",
      phone: "+359 887 222 300",
      email: "ivan.petrov@example.test",
      householdMembers: 4,
      childrenUnder18: 2,
      employmentType: "business_owner",
      employmentMonths: 38,
      permanentContract: false,
      salaryTransferPossible: false
    },
    financial: {
      netMonthlyIncome: 2600,
      coApplicantIncome: 900,
      otherMonthlyIncome: 600,
      existingMonthlyDebt: 480,
      declaredSavings: 26000,
      requestedAmount: 132000,
      propertyValue: 158000,
      ownContribution: 26000,
      termYears: 30,
      purpose: "construction",
      constructionStage: "act14",
      currency: "EUR"
    },
    consents: {
      precontractInfo: true,
      personalDataNotice: true,
      mediationAgreement: false,
      bankSharingConsent: true,
      marketingConsent: false,
      acceptedAt: "2026-07-07T11:40:00.000Z",
      ipAddress: "demo-hidden",
      documentVersion: "2026.07-demo"
    },
    documents: [
      { id: "id-card", label: "Лична карта", status: "received", owner: "client" },
      { id: "tax-return", label: "Годишна данъчна декларация", status: "missing", owner: "client" },
      { id: "company-financials", label: "Финансов отчет на фирма", status: "missing", owner: "client" },
      { id: "construction-docs", label: "Документи за строеж / акт 14", status: "needs_update", owner: "client" }
    ],
    notes: ["Самоучастието е ниско за някои банки.", "Нужен е ръчен преглед за собствен бизнес."],
    preferredBanks: ["Пощенска банка", "Банка ДСК"]
  },
  {
    id: "app-1003",
    createdAt: "2026-07-06T15:10:00.000Z",
    updatedAt: "2026-07-08T09:05:00.000Z",
    status: "bank_form_requested",
    consultantId: "user-consultant-1",
    applicant: {
      fullName: "Николай Димитров",
      age: 29,
      city: "Варна",
      phone: "+359 899 500 600",
      email: "nikolay.dimitrov@example.test",
      householdMembers: 1,
      childrenUnder18: 0,
      employmentType: "permanent_contract",
      employmentMonths: 14,
      permanentContract: true,
      salaryTransferPossible: true
    },
    financial: {
      netMonthlyIncome: 1850,
      coApplicantIncome: 0,
      otherMonthlyIncome: 0,
      existingMonthlyDebt: 530,
      declaredSavings: 3000,
      requestedAmount: 22000,
      propertyValue: 0,
      ownContribution: 0,
      termYears: 7,
      purpose: "consumer",
      constructionStage: "none",
      currency: "EUR"
    },
    consents: {
      precontractInfo: true,
      personalDataNotice: true,
      mediationAgreement: true,
      bankSharingConsent: true,
      marketingConsent: true,
      acceptedAt: "2026-07-06T15:24:00.000Z",
      ipAddress: "demo-hidden",
      documentVersion: "2026.07-demo"
    },
    documents: [
      { id: "id-card", label: "Лична карта", status: "received", owner: "client" },
      { id: "income-proof", label: "Служебна бележка / доход", status: "missing", owner: "client" },
      { id: "credit-report", label: "Справка за текущи кредити", status: "missing", owner: "consultant" }
    ],
    notes: ["Иска обединяване на задължения и ремонт.", "DTI е граничен."],
    preferredBanks: ["ОББ", "Пощенска банка", "Алианц Банк"]
  },
  {
    id: "app-1004",
    createdAt: "2026-07-05T08:30:00.000Z",
    updatedAt: "2026-07-09T10:15:00.000Z",
    status: "auto_qualified",
    consultantId: "user-consultant-1",
    applicant: {
      fullName: "Десислава Колева",
      age: 37,
      city: "Бургас",
      phone: "+359 888 420 210",
      email: "desislava.koleva@example.test",
      householdMembers: 2,
      childrenUnder18: 0,
      employmentType: "permanent_contract",
      employmentMonths: 96,
      permanentContract: true,
      salaryTransferPossible: true
    },
    financial: {
      netMonthlyIncome: 5200,
      coApplicantIncome: 2100,
      otherMonthlyIncome: 0,
      existingMonthlyDebt: 120,
      declaredSavings: 95000,
      requestedAmount: 180000,
      propertyValue: 280000,
      ownContribution: 100000,
      termYears: 25,
      purpose: "home_purchase",
      constructionStage: "act16",
      currency: "EUR"
    },
    consents: {
      precontractInfo: true,
      personalDataNotice: true,
      mediationAgreement: true,
      bankSharingConsent: true,
      marketingConsent: false,
      acceptedAt: "2026-07-05T08:45:00.000Z",
      ipAddress: "demo-hidden",
      documentVersion: "2026.07-demo"
    },
    documents: [
      { id: "id-card", label: "Лична карта", status: "received", owner: "client" },
      { id: "income-proof", label: "Служебна бележка / доход", status: "received", owner: "client" },
      { id: "property-offer", label: "Предварителен договор", status: "received", owner: "client" },
      { id: "bank-statements", label: "Извлечения по сметка 6 месеца", status: "received", owner: "client" }
    ],
    notes: ["Много силен профил за автоматично насочване.", "Подходяща за пакетно сравнение с фиксирана лихва."],
    preferredBanks: ["УниКредит Булбанк", "ОББ", "БАКБ"]
  },
  {
    id: "app-1005",
    createdAt: "2026-07-04T13:05:00.000Z",
    updatedAt: "2026-07-09T11:00:00.000Z",
    status: "missing_documents",
    consultantId: "user-consultant-1",
    applicant: {
      fullName: "Георги Андреев",
      age: 45,
      city: "Русе",
      phone: "+359 887 440 908",
      email: "georgi.andreev@example.test",
      householdMembers: 3,
      childrenUnder18: 1,
      employmentType: "self_employed",
      employmentMonths: 60,
      permanentContract: false,
      salaryTransferPossible: false
    },
    financial: {
      netMonthlyIncome: 3100,
      coApplicantIncome: 0,
      otherMonthlyIncome: 900,
      existingMonthlyDebt: 350,
      declaredSavings: 42000,
      requestedAmount: 98000,
      propertyValue: 155000,
      ownContribution: 57000,
      termYears: 20,
      purpose: "refinance",
      constructionStage: "existing",
      currency: "EUR"
    },
    consents: {
      precontractInfo: true,
      personalDataNotice: true,
      mediationAgreement: true,
      bankSharingConsent: true,
      marketingConsent: true,
      acceptedAt: "2026-07-04T13:20:00.000Z",
      ipAddress: "demo-hidden",
      documentVersion: "2026.07-demo"
    },
    documents: [
      { id: "id-card", label: "Лична карта", status: "received", owner: "client" },
      { id: "tax-return", label: "Данъчна декларация", status: "missing", owner: "client" },
      { id: "income-proof", label: "Извлечение от приходи", status: "needs_update", owner: "client" },
      { id: "property-docs", label: "Нотариален акт / ипотека", status: "received", owner: "client" }
    ],
    notes: ["Рефинансиране с добър LTV, но липсва доказване на доход.", "Да се провери историята на текущия кредит."],
    preferredBanks: ["Банка ДСК", "Fibank"]
  },
  {
    id: "app-1006",
    createdAt: "2026-07-03T16:40:00.000Z",
    updatedAt: "2026-07-09T09:45:00.000Z",
    status: "declined_precheck",
    consultantId: "user-consultant-1",
    applicant: {
      fullName: "Радослав Михайлов",
      age: 31,
      city: "Стара Загора",
      phone: "+359 899 770 118",
      email: "radoslav.mihaylov@example.test",
      householdMembers: 1,
      childrenUnder18: 0,
      employmentType: "temporary_contract",
      employmentMonths: 4,
      permanentContract: false,
      salaryTransferPossible: false
    },
    financial: {
      netMonthlyIncome: 1250,
      coApplicantIncome: 0,
      otherMonthlyIncome: 0,
      existingMonthlyDebt: 720,
      declaredSavings: 1000,
      requestedAmount: 18000,
      propertyValue: 0,
      ownContribution: 0,
      termYears: 6,
      purpose: "consumer",
      constructionStage: "none",
      currency: "EUR"
    },
    consents: {
      precontractInfo: true,
      personalDataNotice: true,
      mediationAgreement: false,
      bankSharingConsent: false,
      marketingConsent: false,
      acceptedAt: "2026-07-03T16:50:00.000Z",
      ipAddress: "demo-hidden",
      documentVersion: "2026.07-demo"
    },
    documents: [
      { id: "id-card", label: "Лична карта", status: "received", owner: "client" },
      { id: "income-proof", label: "Документ за доход", status: "missing", owner: "client" },
      { id: "credit-report", label: "Справка за задължения", status: "missing", owner: "consultant" }
    ],
    notes: ["Висока задлъжнялост и кратка заетост.", "Да не се изпраща към банка без подобрение на профила."],
    preferredBanks: []
  },
  {
    id: "app-1007",
    createdAt: "2026-07-02T09:20:00.000Z",
    updatedAt: "2026-07-09T12:05:00.000Z",
    status: "offer_received",
    consultantId: "user-consultant-1",
    applicant: {
      fullName: "Петя Николова",
      age: 39,
      city: "Велико Търново",
      phone: "+359 886 345 700",
      email: "petya.nikolova@example.test",
      householdMembers: 4,
      childrenUnder18: 2,
      employmentType: "permanent_contract",
      employmentMonths: 108,
      permanentContract: true,
      salaryTransferPossible: true
    },
    financial: {
      netMonthlyIncome: 3600,
      coApplicantIncome: 2400,
      otherMonthlyIncome: 200,
      existingMonthlyDebt: 180,
      declaredSavings: 52000,
      requestedAmount: 118000,
      propertyValue: 175000,
      ownContribution: 57000,
      termYears: 30,
      purpose: "home_purchase",
      constructionStage: "existing",
      currency: "EUR"
    },
    consents: {
      precontractInfo: true,
      personalDataNotice: true,
      mediationAgreement: true,
      bankSharingConsent: true,
      marketingConsent: true,
      acceptedAt: "2026-07-02T09:40:00.000Z",
      ipAddress: "demo-hidden",
      documentVersion: "2026.07-demo"
    },
    documents: [
      { id: "id-card", label: "Лична карта", status: "verified", owner: "consultant" },
      { id: "income-proof", label: "Служебни бележки", status: "verified", owner: "consultant" },
      { id: "property-offer", label: "Документи за имот", status: "verified", owner: "consultant" },
      { id: "bank-statements", label: "Банкови извлечения", status: "verified", owner: "consultant" }
    ],
    notes: ["Получена индикативна оферта, чака сравнение на такси.", "Клиентът предпочита по-ниска първоначална вноска."],
    preferredBanks: ["ОББ", "Пощенска банка", "Централна кооперативна банка"]
  },
  {
    id: "app-1008",
    createdAt: "2026-07-01T14:55:00.000Z",
    updatedAt: "2026-07-09T08:30:00.000Z",
    status: "needs_review",
    consultantId: "user-consultant-1",
    applicant: {
      fullName: "Стефан Костов",
      age: 48,
      city: "София",
      phone: "+359 887 909 334",
      email: "stefan.kostov@example.test",
      householdMembers: 2,
      childrenUnder18: 0,
      employmentType: "business_owner",
      employmentMonths: 144,
      permanentContract: false,
      salaryTransferPossible: false
    },
    financial: {
      netMonthlyIncome: 4800,
      coApplicantIncome: 0,
      otherMonthlyIncome: 1600,
      existingMonthlyDebt: 950,
      declaredSavings: 30000,
      requestedAmount: 90000,
      propertyValue: 0,
      ownContribution: 0,
      termYears: 8,
      purpose: "business",
      constructionStage: "none",
      currency: "EUR"
    },
    consents: {
      precontractInfo: true,
      personalDataNotice: true,
      mediationAgreement: true,
      bankSharingConsent: true,
      marketingConsent: false,
      acceptedAt: "2026-07-01T15:08:00.000Z",
      ipAddress: "demo-hidden",
      documentVersion: "2026.07-demo"
    },
    documents: [
      { id: "id-card", label: "Лична карта", status: "received", owner: "client" },
      { id: "company-financials", label: "Финансов отчет на фирма", status: "received", owner: "client" },
      { id: "tax-return", label: "Данъчна декларация", status: "needs_update", owner: "client" },
      { id: "bank-statements", label: "Фирмени банкови извлечения", status: "missing", owner: "client" }
    ],
    notes: ["Бизнес кредит с нужда от ръчна оценка.", "Да се отделят лични от фирмени приходи."],
    preferredBanks: ["ОББ", "ПроКредит Банк"]
  },
  {
    id: "app-1009",
    createdAt: "2026-06-30T12:10:00.000Z",
    updatedAt: "2026-07-09T12:40:00.000Z",
    status: "bank_ready",
    consultantId: "user-consultant-1",
    applicant: {
      fullName: "Антония Василева",
      age: 33,
      city: "Пловдив",
      phone: "+359 889 601 230",
      email: "antonia.vasileva@example.test",
      householdMembers: 2,
      childrenUnder18: 0,
      employmentType: "permanent_contract",
      employmentMonths: 58,
      permanentContract: true,
      salaryTransferPossible: true
    },
    financial: {
      netMonthlyIncome: 2900,
      coApplicantIncome: 1700,
      otherMonthlyIncome: 0,
      existingMonthlyDebt: 0,
      declaredSavings: 64000,
      requestedAmount: 126000,
      propertyValue: 205000,
      ownContribution: 79000,
      termYears: 27,
      purpose: "home_purchase",
      constructionStage: "act15",
      currency: "EUR"
    },
    consents: {
      precontractInfo: true,
      personalDataNotice: true,
      mediationAgreement: true,
      bankSharingConsent: true,
      marketingConsent: true,
      acceptedAt: "2026-06-30T12:24:00.000Z",
      ipAddress: "demo-hidden",
      documentVersion: "2026.07-demo"
    },
    documents: [
      { id: "id-card", label: "Лична карта", status: "verified", owner: "consultant" },
      { id: "income-proof", label: "Доказване на доход", status: "verified", owner: "consultant" },
      { id: "property-offer", label: "Предварителен договор", status: "verified", owner: "consultant" },
      { id: "construction-docs", label: "Документи акт 15", status: "verified", owner: "consultant" }
    ],
    notes: ["Готов пакет за банки, които приемат акт 15.", "Добър LTV, но да се провери срокът на акт 16."],
    preferredBanks: ["Пощенска банка", "Банка ДСК", "Fibank"]
  }
];

export const bankFormTemplates: BankFormTemplate[] = [
  {
    id: "form-ubb-mortgage-v1",
    bankName: "ОББ",
    productId: "ubb-home-purchase",
    title: "Заявление за ипотечен кредит - ОББ",
    version: "2026.01",
    isActive: true,
    description: "Банкова форма за покупка на жилище с превод на доход и ипотечно обезпечение.",
    requiredConsentScope: "specific_bank",
    requiredDocumentIds: ["id-card", "income-proof", "property-offer", "bank-statements"],
    createdAt: "2026-07-08T09:00:00.000Z",
    updatedAt: "2026-07-09T09:30:00.000Z",
    fields: [
      {
        id: "borrower_full_name",
        label: "Имена на кредитоискател",
        type: "text",
        required: true,
        section: "Лични данни",
        mapsFrom: "applicant.fullName"
      },
      {
        id: "borrower_identifier",
        label: "ЕГН / ЛНЧ",
        type: "text",
        required: true,
        section: "Лични данни",
        helpText: "В продукционна среда това поле трябва да се криптира и маскира в списъци."
      },
      {
        id: "employment_type",
        label: "Тип заетост",
        type: "select",
        required: true,
        section: "Доходи",
        options: ["Трудов договор", "Собствен бизнес", "Свободна професия", "Граждански договор"],
        mapsFrom: "applicant.employmentType"
      },
      {
        id: "net_income",
        label: "Нетен месечен доход",
        type: "number",
        required: true,
        section: "Доходи",
        mapsFrom: "financial.netMonthlyIncome"
      },
      {
        id: "loan_amount",
        label: "Искан размер на кредита",
        type: "number",
        required: true,
        section: "Кредитни параметри",
        mapsFrom: "financial.requestedAmount"
      },
      {
        id: "salary_transfer",
        label: "Клиентът ще превежда доход по сметка в банката",
        type: "yes_no",
        required: true,
        section: "Кредитни параметри",
        mapsFrom: "applicant.salaryTransferPossible"
      },
      {
        id: "property_stage",
        label: "Етап на имота",
        type: "select",
        required: true,
        section: "Обезпечение",
        options: ["Акт 14", "Акт 15", "Акт 16", "Съществуващ имот"],
        mapsFrom: "financial.constructionStage"
      },
      {
        id: "consultant_comment",
        label: "Бележка от кредитен консултант",
        type: "textarea",
        required: false,
        section: "Вътрешен преглед",
        consultantOnly: true
      }
    ]
  },
  {
    id: "form-unicredit-fixed-v1",
    bankName: "УниКредит Булбанк",
    productId: "unicredit-fixed-5",
    title: "Заявление за ипотечен кредит с фиксирана лихва",
    version: "2026.01",
    isActive: true,
    description: "Форма за клиенти, които искат предвидима вноска за първите години.",
    requiredConsentScope: "specific_bank",
    requiredDocumentIds: ["id-card", "income-proof", "property-offer"],
    createdAt: "2026-07-08T09:10:00.000Z",
    updatedAt: "2026-07-09T09:35:00.000Z",
    fields: [
      { id: "borrower_full_name", label: "Имена", type: "text", required: true, section: "Лични данни", mapsFrom: "applicant.fullName" },
      { id: "phone", label: "Телефон", type: "text", required: true, section: "Контакти", mapsFrom: "applicant.phone" },
      { id: "email", label: "Имейл", type: "text", required: true, section: "Контакти", mapsFrom: "applicant.email" },
      { id: "fixed_period_awareness", label: "Клиентът е информиран за периода на фиксирана лихва", type: "checkbox", required: true, section: "Декларации" },
      { id: "requested_amount", label: "Искана сума", type: "number", required: true, section: "Кредитни параметри", mapsFrom: "financial.requestedAmount" },
      { id: "term_years", label: "Срок в години", type: "number", required: true, section: "Кредитни параметри", mapsFrom: "financial.termYears" }
    ]
  },
  {
    id: "form-postbank-consumer-v1",
    bankName: "Пощенска банка",
    productId: "postbank-consumer-online",
    title: "Заявление за потребителски кредит онлайн",
    version: "2026.01",
    isActive: true,
    description: "Форма за потребителски кредит, обединяване на задължения или ремонт.",
    requiredConsentScope: "specific_bank",
    requiredDocumentIds: ["id-card", "income-proof", "credit-report"],
    createdAt: "2026-07-08T10:00:00.000Z",
    updatedAt: "2026-07-09T09:40:00.000Z",
    fields: [
      { id: "borrower_full_name", label: "Имена", type: "text", required: true, section: "Лични данни", mapsFrom: "applicant.fullName" },
      { id: "loan_purpose", label: "Цел на кредита", type: "select", required: true, section: "Кредитни параметри", options: ["Ремонт", "Обединяване на задължения", "Покупка", "Друго"] },
      { id: "requested_amount", label: "Искана сума", type: "number", required: true, section: "Кредитни параметри", mapsFrom: "financial.requestedAmount" },
      { id: "existing_debt", label: "Текущи месечни вноски", type: "number", required: true, section: "Задължения", mapsFrom: "financial.existingMonthlyDebt" },
      { id: "has_overdue_payments", label: "Има ли просрочия през последните 12 месеца", type: "yes_no", required: true, section: "Задължения" },
      { id: "income_document", label: "Документ за доход", type: "file", required: true, section: "Документи" }
    ]
  },
  {
    id: "form-dsk-refinance-v1",
    bankName: "Банка ДСК",
    productId: "dsk-refinance",
    title: "Пакет за рефинансиране",
    version: "2026.01",
    isActive: true,
    description: "Форма за прехвърляне на съществуващ ипотечен кредит и проверка на остатък, обезпечение и такси.",
    requiredConsentScope: "specific_bank",
    requiredDocumentIds: ["id-card", "income-proof", "property-docs", "bank-statements"],
    createdAt: "2026-07-09T10:10:00.000Z",
    updatedAt: "2026-07-09T10:10:00.000Z",
    fields: [
      { id: "borrower_full_name", label: "Имена", type: "text", required: true, section: "Лични данни", mapsFrom: "applicant.fullName" },
      { id: "current_bank", label: "Текуща банка", type: "text", required: true, section: "Текущ кредит" },
      { id: "remaining_principal", label: "Остатък по кредита", type: "number", required: true, section: "Текущ кредит", mapsFrom: "financial.requestedAmount" },
      { id: "current_monthly_payment", label: "Текуща месечна вноска", type: "number", required: true, section: "Текущ кредит", mapsFrom: "financial.existingMonthlyDebt" },
      { id: "property_value", label: "Актуална стойност на имота", type: "number", required: true, section: "Обезпечение", mapsFrom: "financial.propertyValue" },
      { id: "no_overdue_declaration", label: "Клиентът декларира липса на просрочия", type: "checkbox", required: true, section: "Декларации" }
    ]
  },
  {
    id: "form-fibank-mortgage-v1",
    bankName: "Първа инвестиционна банка",
    productId: "fibank-home-flex",
    title: "Заявление за гъвкав жилищен кредит",
    version: "2026.01",
    isActive: true,
    description: "Форма за ипотечен кредит при стандартен имот, акт 15/16 и проверка на собствено участие.",
    requiredConsentScope: "specific_bank",
    requiredDocumentIds: ["id-card", "income-proof", "property-offer", "construction-docs"],
    createdAt: "2026-07-09T10:20:00.000Z",
    updatedAt: "2026-07-09T10:20:00.000Z",
    fields: [
      { id: "borrower_full_name", label: "Имена", type: "text", required: true, section: "Лични данни", mapsFrom: "applicant.fullName" },
      { id: "city", label: "Град", type: "text", required: true, section: "Лични данни", mapsFrom: "applicant.city" },
      { id: "requested_amount", label: "Искана сума", type: "number", required: true, section: "Кредитни параметри", mapsFrom: "financial.requestedAmount" },
      { id: "own_contribution", label: "Самоучастие", type: "number", required: true, section: "Кредитни параметри", mapsFrom: "financial.ownContribution" },
      { id: "construction_stage", label: "Етап на имота", type: "select", required: true, section: "Обезпечение", options: ["Акт 15", "Акт 16", "Съществуващ имот"], mapsFrom: "financial.constructionStage" },
      { id: "valuation_ready", label: "Има ли предварителна оценка на имота", type: "yes_no", required: true, section: "Обезпечение" }
    ]
  },
  {
    id: "form-tbi-consumer-v1",
    bankName: "TBI Bank",
    productId: "tbi-consumer-fast",
    title: "Бърза потребителска заявка",
    version: "2026.01",
    isActive: true,
    description: "Кратка форма за потребителски кредит с фокус върху доход, задължения и цел на кредита.",
    requiredConsentScope: "specific_bank",
    requiredDocumentIds: ["id-card", "income-proof"],
    createdAt: "2026-07-09T10:35:00.000Z",
    updatedAt: "2026-07-09T10:35:00.000Z",
    fields: [
      { id: "borrower_full_name", label: "Имена", type: "text", required: true, section: "Лични данни", mapsFrom: "applicant.fullName" },
      { id: "phone", label: "Телефон", type: "text", required: true, section: "Контакти", mapsFrom: "applicant.phone" },
      { id: "requested_amount", label: "Искана сума", type: "number", required: true, section: "Кредит", mapsFrom: "financial.requestedAmount" },
      { id: "declared_income", label: "Деклариран доход", type: "number", required: true, section: "Доходи", mapsFrom: "financial.netMonthlyIncome" },
      { id: "purpose_description", label: "Описание на целта", type: "textarea", required: true, section: "Кредит" },
      { id: "data_sharing", label: "Съгласие за автоматизирана проверка", type: "checkbox", required: true, section: "Декларации" }
    ]
  },
  {
    id: "form-ubb-business-v1",
    bankName: "ОББ",
    productId: "ubb-small-business",
    title: "Бизнес кредит - първична оценка",
    version: "2026.01",
    isActive: true,
    description: "Форма за фирмен кредит с обороти, сектор, отчети и консултантски коментар.",
    requiredConsentScope: "specific_bank",
    requiredDocumentIds: ["id-card", "company-financials", "tax-return", "bank-statements"],
    createdAt: "2026-07-09T10:45:00.000Z",
    updatedAt: "2026-07-09T10:45:00.000Z",
    fields: [
      { id: "owner_full_name", label: "Имена на собственик/управител", type: "text", required: true, section: "Лични данни", mapsFrom: "applicant.fullName" },
      { id: "company_name", label: "Име на фирма", type: "text", required: true, section: "Фирмени данни" },
      { id: "annual_turnover", label: "Годишен оборот", type: "number", required: true, section: "Фирмени данни" },
      { id: "requested_amount", label: "Искана сума", type: "number", required: true, section: "Кредит", mapsFrom: "financial.requestedAmount" },
      { id: "loan_purpose", label: "Цел на бизнес кредита", type: "textarea", required: true, section: "Кредит" },
      { id: "consultant_risk_note", label: "Рискова бележка", type: "textarea", required: false, section: "Вътрешен преглед", consultantOnly: true }
    ]
  }
];

export const bankFormRequests: BankFormRequest[] = [
  {
    id: "req-ubb-1001",
    applicationId: "app-1001",
    templateId: "form-ubb-mortgage-v1",
    requestedBy: "user-consultant-1",
    assignedToUserId: "user-client-1",
    status: "in_progress",
    dueDate: "2026-07-12",
    requestedAt: "2026-07-09T08:50:00.000Z",
    consultantNote: "Моля, потвърдете предварително попълнените данни и добавете ЕГН само в защитената форма.",
    values: {
      borrower_full_name: "Мария Георгиева",
      employment_type: "Трудов договор",
      net_income: 4300,
      loan_amount: 145000,
      salary_transfer: true,
      property_stage: "Акт 16"
    },
    correctionNotes: []
  },
  {
    id: "req-postbank-1003",
    applicationId: "app-1003",
    templateId: "form-postbank-consumer-v1",
    requestedBy: "user-consultant-1",
    assignedToUserId: "user-client-1",
    status: "requested",
    dueDate: "2026-07-11",
    requestedAt: "2026-07-09T09:10:00.000Z",
    consultantNote: "Попълнете целта на кредита и потвърдете задълженията преди банков пакет.",
    values: {
      borrower_full_name: "Николай Димитров",
      requested_amount: 22000,
      existing_debt: 530
    },
    correctionNotes: []
  },
  {
    id: "req-dsk-1005",
    applicationId: "app-1005",
    templateId: "form-dsk-refinance-v1",
    requestedBy: "user-consultant-1",
    assignedToUserId: "user-client-1",
    status: "needs_correction",
    dueDate: "2026-07-13",
    requestedAt: "2026-07-09T10:30:00.000Z",
    consultantNote: "Добавете актуален остатък по кредита и обновена данъчна декларация.",
    values: {
      borrower_full_name: "Георги Андреев",
      remaining_principal: 98000,
      current_monthly_payment: 350,
      property_value: 155000
    },
    correctionNotes: ["Липсва текуща банка и документ за доход."]
  },
  {
    id: "req-fibank-1009",
    applicationId: "app-1009",
    templateId: "form-fibank-mortgage-v1",
    requestedBy: "user-consultant-1",
    assignedToUserId: "user-client-1",
    status: "verified",
    dueDate: "2026-07-10",
    requestedAt: "2026-07-08T14:30:00.000Z",
    submittedAt: "2026-07-09T08:20:00.000Z",
    consultantNote: "Проверена форма за акт 15 маршрут.",
    values: {
      borrower_full_name: "Антония Василева",
      city: "Пловдив",
      requested_amount: 126000,
      own_contribution: 79000,
      construction_stage: "Акт 15",
      valuation_ready: true
    },
    correctionNotes: []
  },
  {
    id: "req-ubb-business-1008",
    applicationId: "app-1008",
    templateId: "form-ubb-business-v1",
    requestedBy: "user-consultant-1",
    assignedToUserId: "user-client-1",
    status: "requested",
    dueDate: "2026-07-14",
    requestedAt: "2026-07-09T11:05:00.000Z",
    consultantNote: "Попълнете фирмените данни и качете последните банкови извлечения.",
    values: {
      owner_full_name: "Стефан Костов",
      requested_amount: 90000
    },
    correctionNotes: []
  }
];

export const clientTasks: ClientTask[] = [
  {
    id: "task-bank-form-1001",
    applicationId: "app-1001",
    type: "bank_form",
    title: "Попълнете банкова форма за ОББ",
    description: "Кредитният консултант изиска форма за ипотечен кредит. Проверете данните и попълнете липсващите полета.",
    status: "in_progress",
    dueDate: "2026-07-12",
    relatedRequestId: "req-ubb-1001",
    createdAt: "2026-07-09T08:50:00.000Z"
  },
  {
    id: "task-bank-statements-1001",
    applicationId: "app-1001",
    type: "document",
    title: "Качете извлечения по сметка",
    description: "Необходими са извлечения за последните 6 месеца преди изпращане към банка.",
    status: "open",
    dueDate: "2026-07-12",
    createdAt: "2026-07-09T08:55:00.000Z"
  },
  {
    id: "task-consent-1001",
    applicationId: "app-1001",
    type: "consent",
    title: "Потвърдете споделяне към ОББ",
    description: "Преди изпращане към конкретна банка се пази отделно доказателство за съгласие.",
    status: "open",
    dueDate: "2026-07-12",
    relatedRequestId: "req-ubb-1001",
    createdAt: "2026-07-09T08:57:00.000Z"
  },
  {
    id: "task-tax-return-1005",
    applicationId: "app-1005",
    type: "document",
    title: "Обновете данъчна декларация",
    description: "За рефинансиране е необходима последна декларация и разбивка на доходите като свободна професия.",
    status: "open",
    dueDate: "2026-07-13",
    relatedRequestId: "req-dsk-1005",
    createdAt: "2026-07-09T10:31:00.000Z"
  },
  {
    id: "task-business-bank-statements-1008",
    applicationId: "app-1008",
    type: "bank_form",
    title: "Попълнете бизнес форма за ОББ",
    description: "Изискани са фирмени данни, оборот и банкови извлечения за първична бизнес оценка.",
    status: "open",
    dueDate: "2026-07-14",
    relatedRequestId: "req-ubb-business-1008",
    createdAt: "2026-07-09T11:05:00.000Z"
  },
  {
    id: "task-fibank-1009",
    applicationId: "app-1009",
    type: "message",
    title: "Потвърдете финален банков пакет",
    description: "Формата е проверена и досието е готово за изпращане към избраните банки.",
    status: "done",
    dueDate: "2026-07-10",
    relatedRequestId: "req-fibank-1009",
    createdAt: "2026-07-09T08:25:00.000Z"
  },
  {
    id: "task-consents-1006",
    applicationId: "app-1006",
    type: "consent",
    title: "Липсват съгласия за банкова проверка",
    description: "Без съгласие за споделяне заявката остава спряна при предварителна оценка.",
    status: "overdue",
    dueDate: "2026-07-06",
    createdAt: "2026-07-03T16:55:00.000Z"
  }
];

export const ruleDefinitions: RuleDefinition[] = [
  {
    id: "rule-dti-base",
    name: "Съотношение дълг/доход под 50%",
    category: "risk",
    description: "Общите месечни задължения след новата вноска трябва да са под 50% от доказания доход.",
    severity: "warning",
    active: true
  },
  {
    id: "rule-ltv-mortgage",
    name: "Съотношение кредит/стойност на имота под 85%",
    category: "eligibility",
    description: "Ипотечните заявки с LTV над 85% не се изпращат автоматично към банка.",
    severity: "blocker",
    active: true
  },
  {
    id: "rule-consents",
    name: "Пълни съгласия",
    category: "documents",
    description: "Споделяне към банка се разрешава само при приети документи и съгласие за банково споделяне.",
    severity: "blocker",
    active: true
  },
  {
    id: "rule-employment",
    name: "Стабилна заетост",
    category: "risk",
    description: "Под 6 месеца заетост винаги изисква консултантски преглед.",
    severity: "warning",
    active: true
  },
  {
    id: "rule-act14",
    name: "Строеж акт 14",
    category: "routing",
    description: "Заявки за ранно строителство се насочват само към банки, които приемат този етап.",
    severity: "warning",
    active: true
  },
  {
    id: "rule-self-employed-docs",
    name: "Самоосигуряващи се - пълен доходен пакет",
    category: "documents",
    description: "Свободна професия и собствен бизнес изискват данъчна декларация, банкови извлечения и поне един отчетен период.",
    severity: "blocker",
    active: true
  },
  {
    id: "rule-auto-route-low-risk",
    name: "Автоматично насочване само при нисък риск",
    category: "routing",
    description: "Автоматично изпращане към банка се разрешава само при нисък риск, пълни съгласия и поне два подходящи продукта.",
    severity: "blocker",
    active: true
  },
  {
    id: "rule-consumer-fast-lane",
    name: "Бърз потребителски маршрут",
    category: "routing",
    description: "Потребителски кредит до 25 000 EUR с нисък DTI може да се насочи към дигитален канал без консултантска среща.",
    severity: "info",
    active: true
  },
  {
    id: "rule-offer-expiry",
    name: "Контрол на срок на оферта",
    category: "risk",
    description: "Получените оферти трябва да имат срок, условия за промяна и отговорник за последващо действие.",
    severity: "warning",
    active: true
  },
  {
    id: "rule-gdpr-minimization",
    name: "Минимизиране на лични данни",
    category: "documents",
    description: "ЕГН, лични документи и файлове се събират само във формите, за които има конкретна банкова цел и съгласие.",
    severity: "blocker",
    active: true
  }
];

export const auditEvents: AuditEvent[] = [
  {
    id: "audit-1",
    at: "2026-07-08T13:21:00.000Z",
    actor: "Двигател за правила",
    action: "Преизчислена оценка",
    target: "app-1001",
    details: "Оценка 86, маршрут: консултантски преглед заради липсващо банково извлечение."
  },
  {
    id: "audit-2",
    at: "2026-07-08T10:13:00.000Z",
    actor: "Елена Стоянова",
    action: "Добавена бележка",
    target: "app-1002",
    details: "Изискани документи за собствен бизнес и строеж."
  },
  {
    id: "audit-3",
    at: "2026-07-07T16:04:00.000Z",
    actor: "Администратор",
    action: "Актуализиран продукт",
    target: "unicredit-fixed-5",
    details: "Обновена демо лихва и източник от публична страница."
  },
  {
    id: "audit-4",
    at: "2026-07-09T10:31:00.000Z",
    actor: "Елена Стоянова",
    action: "Изискана корекция",
    target: "req-dsk-1005",
    details: "Рефинансиране: липсват текуща банка и последна данъчна декларация."
  },
  {
    id: "audit-5",
    at: "2026-07-09T08:25:00.000Z",
    actor: "Елена Стоянова",
    action: "Проверена банкова форма",
    target: "req-fibank-1009",
    details: "Пакетът за Fibank е маркиран като проверен и готов за банково изпращане."
  },
  {
    id: "audit-6",
    at: "2026-07-09T11:06:00.000Z",
    actor: "Двигател за правила",
    action: "Бизнес профил за ръчен преглед",
    target: "app-1008",
    details: "Цел бизнес кредит: автоматичното насочване е ограничено до консултантски маршрут."
  },
  {
    id: "audit-7",
    at: "2026-07-09T12:41:00.000Z",
    actor: "Елена Стоянова",
    action: "Маркирано готово досие",
    target: "app-1009",
    details: "Всички документи са проверени; остава избор на финални банки."
  },
  {
    id: "audit-8",
    at: "2026-07-09T09:46:00.000Z",
    actor: "Двигател за правила",
    action: "Спряна предварителна оценка",
    target: "app-1006",
    details: "Кратка заетост, висок DTI и липсващи съгласия блокират автоматично насочване."
  }
];

export const productionChecklist = [
  {
    label: "Реална автентикация",
    status: "next",
    detail: "Cloudflare Access, Supabase Auth или собствен вход с многофакторна защита за консултанти."
  },
  {
    label: "D1/Postgres база",
    status: "ready-shape",
    detail: "Моделът вече е разделен на заявления, продукти, банкови форми, задачи, съгласия и журнал."
  },
  {
    label: "Електронен подпис",
    status: "next",
    detail: "Интеграция с eIDAS доставчик за договори, ако правният процес го изисква."
  },
  {
    label: "Срокове за съхранение по GDPR",
    status: "next",
    detail: "Автоматично изтриване/анонимизиране след договорения срок и регистър на достъпите."
  },
  {
    label: "Банкови форми",
    status: "ready-shape",
    detail: "Администраторът управлява версии на форми, а консултантът ги възлага като задачи към клиент."
  },
  {
    label: "Обяснима автоматизация",
    status: "ready-shape",
    detail: "Всяко банково съвпадение има причини, блокиращи фактори и човек в процеса при гранични случаи."
  }
];
